import {
  AfterViewInit,
  ChangeDetectorRef,
  Directive,
  effect,
  ElementRef,
  HostBinding,
  inject,
  Input,
  OnChanges,
  SimpleChanges
} from '@angular/core';
import { ButtonTypes } from '@shared/components/input/button/button-types';
import { LoadingService } from '@app/core/services/loading.service';

/**
 * Directive to style and configure button elements with predefined classes and types.
 *
 * This directive automatically applies a specific class to the host element based on the provided `type`.
 * Additional custom classes can be set via the `class` input property.
 *
 * Example usage:
 *
 * ```html
 * <button appButton [type]="'secondary'" (click)="yourFunction()">Click me</button>
 * ```
 */
@Directive({
  selector: '[appButton]'
})
export class ButtonDirective implements OnChanges, AfterViewInit {
  private readonly cdr = inject(ChangeDetectorRef);

  /**
   * Defines the classes to be applied to the button element.
   * By default, the `ec-button` class is used.
   *
   * Example:
   * ```html
   * <button appButton [class]="'custom-class'"></button>
   * ```
   */
  @HostBinding('class')
  @Input() public class: string = 'ec-button';

  /**
   * Specifies the type of the button. This determines the additional styling applied.
   * Default type is `'primary'`.
   *
   * Possible values depend on the `ButtonTypes` definition (e.g., `'primary'`, `'secondary'`).
   *
   * Example:
   * ```html
   * <button appButton [type]="'secondary'"></button>
   * ```
   */
  @Input() public type: ButtonTypes = 'primary';

  @HostBinding('attr.disabled')
  public get disabledAttr(): true | null {
    return this.disabled ? true : null;
  }

  @Input() public disabled: boolean = false;

  @Input() public allowLoadingState: boolean = true;

  private inputDisabled: boolean = false;

  /**
   * Constructor to inject the button's native element reference.
   *
   * @param element - Access to the `ElementRef` representing the host DOM element.
   * @param loadingService - Service used to manage global loading state.
   */
  constructor(
    private readonly element: ElementRef,
    private readonly loadingService: LoadingService,
  ) {
    effect(() => {
      const isLoading = this.loadingService.loading() && this.allowLoadingState;
      this.disabled = this.inputDisabled || isLoading;

      // Trigger change detection to update the host binding
      this.cdr.markForCheck();
    });
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes['disabled']) {
      this.inputDisabled = changes['disabled'].currentValue;
      this.disabled = this.inputDisabled || (this.loadingService.loading() && this.allowLoadingState);
    }
  }

  /**
   * Lifecycle hook that runs after the view initializes.
   *
   * Adds a type-specific class (`ec-button-{type}`) to the button element for styling purposes.
   */
  public ngAfterViewInit(): void {
    if (this.element) {
      this.element.nativeElement.classList.add(`ec-button-${this.type}`);
    }
  }
}
